import React, { useCallback, useMemo, useState, type ReactNode } from 'react';
import { styled } from '@compiled/react';
import kebabCase from 'lodash/kebabCase';
import Button from '@atlaskit/button';
import { ErrorMessage, Field, HelperMessage } from '@atlaskit/form';
import { Box, Text } from '@atlaskit/primitives';
import SectionMessage from '@atlaskit/section-message';
import { CheckboxSelect, type MenuListComponentProps, type ValueType } from '@atlaskit/select';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';

import { CLICKUP_MAX_LISTS } from '../../../../common/constants';
import type { ThirdPartyEntity } from '../../../../common/types';
import {
	convertOptionToEntity,
	getMultipleSelectedAndAllOptions,
	type OptionType,
} from '../../../../common/ui/custom-select-components/utils';
import { MenuWithFooter } from './menu-with-footer';
import messages from './messages';

const MINIMUM_MENU_HEIGHT = 400;

export type Props = {
	label: string;
	placeholder: string;
	noEntitiesFoundHeader: string;
	noEntitiesFoundDescription: string;
	errorOccurredHeader: string;
	onRetry: () => void;
	entities: ThirdPartyEntity[] | null;
	selectedEntities: ThirdPartyEntity[] | null;
	isLoading: boolean;
	isDisabled?: boolean;
	isRequired?: boolean;
	autoFocus?: boolean;
	isClearable?: boolean;
	openMenuOnFocus?: boolean;
	hasErrorOccurred: boolean;
	onChange: (updatedEntities: ThirdPartyEntity[]) => void;
	placeholderColor?: string;
	onClear?: () => void;
	preselectOnlyOption?: boolean;
};

export const EntitySelectorCheckbox = ({
	label,
	placeholder,
	entities,
	selectedEntities,
	isLoading,
	isDisabled = false,
	autoFocus = false,
	openMenuOnFocus = false,
	onChange,
	onClear,
	hasErrorOccurred,
	noEntitiesFoundHeader,
	noEntitiesFoundDescription,
	errorOccurredHeader,
	onRetry,
	isRequired,
}: Props) => {
	const { formatMessage } = useIntl();
	const [error, setError] = useState<string | null>(null);

	const validateField = useCallback(
		(optionValues?: ValueType<OptionType, true> | null) => {
			if (optionValues && optionValues.length > CLICKUP_MAX_LISTS) {
				setError(
					formatMessage(messages.maxListError, {
						maxListCount: CLICKUP_MAX_LISTS,
					}),
				);
			} else {
				setError(null);
			}
		},
		[formatMessage],
	);

	const onSelectAll = useCallback(() => {
		if (entities && entities.length > 0) {
			onChange(entities);
		}
	}, [entities, onChange]);

	const onChangeHandler = useCallback(
		(optionValues: ValueType<OptionType, true>) => {
			if (optionValues === null && onClear) {
				onClear();
				return;
			}
			const selectedOptions = optionValues?.map((option) => convertOptionToEntity(option));

			selectedOptions && onChange(selectedOptions);
			validateField(optionValues);
		},
		[onChange, onClear, validateField],
	);

	const { selectedOptions, options } = useMemo(
		() => getMultipleSelectedAndAllOptions(entities, selectedEntities),
		[entities, selectedEntities],
	);

	if (!isLoading && entities && entities.length === 0) {
		return (
			<Box paddingBlockStart="space.200">
				<SectionMessage title={noEntitiesFoundHeader} appearance="warning">
					<Text>{noEntitiesFoundDescription}</Text>
				</SectionMessage>
			</Box>
		);
	}

	return (
		<Wrapper data-testid="project-create-importer-upload-flow.ui.select-third-party-entity-screen.entity-content.entity-selector-checkbox.wrapper">
			<Field label={label} name={`selector-${kebabCase(label)}`} isRequired={isRequired}>
				{({ fieldProps: { id, ...otherFieldProps } }) => (
					<>
						<CheckboxSelect
							id={`entityMultiDropdown-${kebabCase(label)}`}
							{...otherFieldProps}
							inputId={`entityMultiDropdown-input-${kebabCase(label)}`}
							name="entityMultiDropdown"
							options={options}
							value={selectedOptions}
							onChange={onChangeHandler}
							placeholder={placeholder}
							isClearable
							isLoading={isLoading}
							isDisabled={isDisabled || hasErrorOccurred}
							autoFocus={autoFocus}
							openMenuOnFocus={openMenuOnFocus}
							minMenuHeight={MINIMUM_MENU_HEIGHT}
							isMulti
							isInvalid={!!error}
							menuPlacement="auto"
							components={{
								MenuList: (menuListProps: MenuListComponentProps<OptionType, true>): ReactNode => (
									<MenuWithFooter
										{...menuListProps}
										showSelectAll={options.length <= CLICKUP_MAX_LISTS && !isLoading}
										onSelectAllClick={onSelectAll}
									/>
								),
							}}
						/>
						{!error && (
							<HelperMessage>
								{formatMessage(messages.maxListHelperText, {
									maxListCount: CLICKUP_MAX_LISTS,
								})}
							</HelperMessage>
						)}
						{error && <ErrorMessage>{error}</ErrorMessage>}
						{hasErrorOccurred && (
							<Box paddingBlockStart="space.200">
								<SectionMessage title={errorOccurredHeader} appearance="warning">
									<Button onClick={onRetry} appearance="link" spacing="none">
										{formatMessage(messages.tryAgain)}
									</Button>
								</SectionMessage>
							</Box>
						)}
					</>
				)}
			</Field>
		</Wrapper>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Wrapper = styled.div({
	marginTop: token('space.300', '24px'),
	width: '100%',
});
